import { Ban } from "lucide-react";
import { useTranslation } from "react-i18next";

interface QuotedMessagePreviewProps {
  text: string | null;
  fromMe: boolean | null;
  /** Quando a citada não tem texto, o tipo de mídia vira o rótulo da prévia. */
  mediaKind?: string | null;
  /** Nome do contato, usado quando a citação é de uma mensagem dele. */
  contactName?: string | null;
  /** `bubble` renderiza dentro da mensagem; `composer` na barra acima do input. */
  variant?: "bubble" | "composer";
  onClick?: () => void;
}

/**
 * Bloco de citação no estilo WhatsApp: barra colorida à esquerda, autor e uma
 * prévia do texto. Usado tanto dentro da bolha quanto na barra do compositor.
 */
export const QuotedMessagePreview = ({
  text,
  fromMe,
  mediaKind,
  contactName,
  variant = "bubble",
  onClick,
}: QuotedMessagePreviewProps) => {
  const { t } = useTranslation("chat");

  const author = fromMe === null
    ? t("area.replyQuoted")
    : fromMe
      ? t("area.replyYou")
      : (contactName || t("area.replyContact"));

  const isClickable = typeof onClick === "function";

  // Sem texto (foto/áudio sem legenda), o tipo de mídia é a prévia — igual ao WhatsApp.
  const mediaLabel = (() => {
    if (!mediaKind) return null;
    if (mediaKind.startsWith("image")) return t("area.replyPhoto");
    if (mediaKind.startsWith("video")) return t("area.replyVideo");
    if (mediaKind.startsWith("audio")) return t("area.replyAudio");
    if (mediaKind.startsWith("document")) return t("area.replyDocument");
    return null;
  })();

  return (
    <div
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      className={`flex flex-col gap-0.5 overflow-hidden rounded-md border-l-[3px] border-primary bg-black/5 px-2 py-1 dark:bg-white/10 ${
        variant === "bubble" ? "mb-1.5" : ""
      } ${isClickable ? "cursor-pointer hover:bg-black/10 dark:hover:bg-white/15" : ""}`}
    >
      <span className="truncate text-[11px] font-medium text-primary">{author}</span>
      {text ? (
        <span className="line-clamp-2 break-words text-[12px] text-muted-foreground dark:text-neutral-300">
          {text}
        </span>
      ) : mediaLabel ? (
        <span className="text-[12px] text-muted-foreground dark:text-neutral-300">{mediaLabel}</span>
      ) : (
        <span className="flex items-center gap-1 text-[12px] italic text-muted-foreground dark:text-neutral-400">
          <Ban className="h-3 w-3 shrink-0" />
          {t("area.replyUnavailable")}
        </span>
      )}
    </div>
  );
};
